import { API_BASE } from "@/lib/api-config";
import { authFetch } from "@/lib/authenticated-fetch";
import { getAccessToken } from "@/lib/local-auth";

/**
 * Basis URL untuk Telegram notes (spawn Python di server). Default mengikuti payroll API — set
 * `REACT_APP_TELEGRAM_NOTES_API_URL` jika layanan lain port/host (mis. :2017).
 */
export const TELEGRAM_NOTES_API_BASE = (process.env.REACT_APP_TELEGRAM_NOTES_API_URL ?? API_BASE).replace(/\/$/, "");

const EXPORT_TARGETS = [
  {
    sync_chat_id: "-1001885449577",
    chat_id: "-1001885449577",
    db: "telegram_reimburse.sqlite",
    kind: "reimburse" as const,
  },
  {
    sync_chat_id: "-1001859972369",
    chat_id: "-1001859972369",
    db: "telegram_hivedesk.sqlite",
    kind: "hivedesk" as const,
  },
];

const MONTH_ID = [
  "januari",
  "februari",
  "maret",
  "april",
  "mei",
  "juni",
  "juli",
  "agustus",
  "september",
  "oktober",
  "november",
  "desember",
] as const;

function messageFromApiErrorBody(body: { error?: unknown }): string {
  const e = body.error;
  if (typeof e === "string") return e;
  return "Permintaan gagal";
}

function requireToken(): string {
  const token = getAccessToken();
  if (!token) throw new Error("Belum login atau token tidak tersedia.");
  return token;
}

/** Akhir kalender gregorian untuk bulan `month1` (1–12). */
function lastCalendarDay(month1: number, year: number): number {
  return new Date(year, month1, 0).getDate();
}

/**
 * Tanggal luar periode penggajian tapi konsisten untuk ekspor arsip Telegram (tanggal penuh di bulan tersebut).
 */
export function calendarMonthIsoRange(month1: number, year: number): { from_date: string; to_date: string } {
  const m = String(month1).padStart(2, "0");
  const from_date = `${year}-${m}-01`;
  const last = lastCalendarDay(month1, year);
  const to_date = `${year}-${m}-${String(last).padStart(2, "0")}`;
  return { from_date, to_date };
}

/** Nama file .md sama seperti pola februari2026_reimburse.md (bulan Indonesia + tahun). */
export function telegramMarkdownFilenamesForMonth(month1: number, year: number): {
  reimburse: string;
  hivedesk: string;
} {
  const stem = `${MONTH_ID[month1 - 1]}${year}`;
  return { reimburse: `${stem}_reimburse.md`, hivedesk: `${stem}_hivedesk.md` };
}

async function parseResponse(res: Response): Promise<unknown> {
  const data = (await res.json().catch(() => ({}))) as { error?: unknown };
  if (!res.ok) throw new Error(messageFromApiErrorBody(data));
  return data;
}

export type TelegramExportRequestBody = {
  sync_chat_id: string;
  chat_id: string;
  from_date: string;
  to_date: string;
  filename: string;
  db: string;
};

export async function exportTelegramNotesMarkdown(payload: TelegramExportRequestBody): Promise<unknown> {
  const token = requireToken();
  const res = await authFetch(`${TELEGRAM_NOTES_API_BASE}/api/v1/telegram-notes/export`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function saveAdditingHoursFromNotes(body: {
  period_id: number;
  filename: string;
  mode: "append" | "replace";
}): Promise<unknown> {
  const token = requireToken();
  const res = await authFetch(`${TELEGRAM_NOTES_API_BASE}/api/v1/telegram-notes/save-additing-hours`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return parseResponse(res);
}

export async function saveReimburseFromNotes(body: {
  period_id: number;
  filename: string;
  mode: "append" | "replace";
}): Promise<unknown> {
  const token = requireToken();
  const res = await authFetch(`${TELEGRAM_NOTES_API_BASE}/api/v1/telegram-notes/save-reimburse`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return parseResponse(res);
}

/** Dua request export (reimburse + HiveDesk) untuk bulan periode penggajian yang dipilih. */
export function getTelegramDualExportBodies(args: {
  month1: number;
  year: number;
  reimburseFilename: string;
  hivedeskFilename: string;
}): Array<{ kind: "reimburse" | "hivedesk"; payload: TelegramExportRequestBody }> {
  const { from_date, to_date } = calendarMonthIsoRange(args.month1, args.year);
  return EXPORT_TARGETS.map((t) => {
    const filename = t.kind === "reimburse" ? args.reimburseFilename : args.hivedeskFilename;
    const payload: TelegramExportRequestBody = {
      sync_chat_id: t.sync_chat_id,
      chat_id: t.chat_id,
      from_date,
      to_date,
      filename,
      db: t.db,
    };
    return { kind: t.kind, payload };
  });
}
